import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Stack,
  Typography,
  Chip,
  IconButton,
  Paper,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Swal from 'sweetalert2';
import config from '../../Config';

const AddProductForm = ({ onCancel, onSuccess }) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState('');

  const [form, setForm] = useState({
    category: '',
    subCategory: '',
    name: '',
    description: '',
    keyFeatureInput: '',
    keyFeatures: [],
    isActive: true,
  });

  const token = localStorage.getItem('token');

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${config.apiBaseUrl}/admin/upsale/category`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (res.ok) {
        setCategories(data.categories || []);
      } else {
        Swal.fire('Error', data.message || 'Failed to load categories.', 'error');
      }
    } catch {
      Swal.fire('Error', 'Unable to fetch categories.', 'error');
    }
  }, [token]);

  const fetchSubCategories = useCallback(
    async (categoryId) => {
      setSubLoading(true);

      try {
        const res = await fetch(`${config.apiBaseUrl}/admin/upsale/subcategory`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();

        if (res.ok) {
          const filtered = (data.subcategories || []).filter((sub) => sub.category === categoryId);
          setSubCategories(filtered);
        } else {
          Swal.fire('Error', data.message || 'Failed to load subcategories.', 'error');
        }
      } catch {
        Swal.fire('Error', 'Unable to fetch subcategories.', 'error');
      } finally {
        setSubLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (form.category) {
      fetchSubCategories(form.category);
      setForm((prev) => ({ ...prev, subCategory: '' }));
    } else {
      setSubCategories([]);
      setForm((prev) => ({ ...prev, subCategory: '' }));
    }
  }, [form.category, fetchSubCategories]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      Swal.fire('Error', 'Only image files are allowed', 'error');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      Swal.fire('Error', 'Image must be less than 2MB', 'error');
      return;
    }

    setImageFile(file);

    if (preview) URL.revokeObjectURL(preview);

    setPreview(URL.createObjectURL(file));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileChange(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const addKeyFeature = () => {
    const feature = form.keyFeatureInput.trim();
    if (feature) {
      setForm((prev) => ({
        ...prev,
        keyFeatures: [...prev.keyFeatures, feature],
        keyFeatureInput: '',
      }));
    }
  };

  const removeKeyFeature = (index) => {
    const updated = [...form.keyFeatures];
    updated.splice(index, 1);
    setForm((prev) => ({ ...prev, keyFeatures: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!imageFile) {
      Swal.fire('Error', 'Please upload an image', 'error');
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append('category', form.category);
    formData.append('subCategory', form.subCategory);
    formData.append('name', form.name);
    formData.append('description', form.description);
    formData.append('isActive', form.isActive);
    formData.append('keyFeatures', JSON.stringify(form.keyFeatures));
    formData.append('image', imageFile);

    try {
      const res = await fetch(`${config.apiBaseUrl}/admin/upsale/product/create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await res.json();

      if (res.ok) {
        Swal.fire('Success', data.message || 'Product added successfully!', 'success');

        setForm({
          category: '',
          subCategory: '',
          name: '',
          description: '',
          keyFeatureInput: '',
          keyFeatures: [],
          isActive: true,
        });

        setImageFile(null);
        setPreview('');

        onSuccess?.();
      } else {
        Swal.fire('Error', data.message || 'Failed to add product', 'error');
      }
    } catch {
      Swal.fire('Error', 'Something went wrong while submitting.', 'error');
    }

    setIsSubmitting(false);
  };

  return (
    <Paper sx={{ p: 4, maxWidth: 900, mx: 'auto', mt: 4, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <IconButton onClick={onCancel}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight="bold">
          Add New Product
        </Typography>
      </Stack>

      <Box component="form" onSubmit={handleSubmit}>
        <Stack direction={isSmDown ? 'column' : 'row'} spacing={3}>
          {/* LEFT */}
          <Stack spacing={2} sx={{ flex: 1 }}>
            <TextField
              select
              label="Category"
              name="category"
              value={form.category}
              onChange={handleInputChange}
              required
              fullWidth
            >
              {categories.map((cat) => (
                <MenuItem key={cat._id} value={cat._id}>
                  {cat.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Subcategory"
              name="subCategory"
              value={form.subCategory}
              onChange={handleInputChange}
              disabled={!form.category || subLoading}
              fullWidth
            >
              {subLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                subCategories.map((sub) => (
                  <MenuItem key={sub._id} value={sub._id}>
                    {sub.name}
                  </MenuItem>
                ))
              )}
            </TextField>

            <TextField
              label="Product Name"
              name="name"
              value={form.name}
              onChange={handleInputChange}
              required
              fullWidth
            />

            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                bgcolor: '#fafafa',
              }}
            >
              <input
                type="file"
                hidden
                id="image-upload"
                accept="image/*"
                onChange={(e) => handleFileChange(e.target.files[0])}
              />

              {!preview ? (
                <label htmlFor="image-upload">
                  <Typography>
                    Drag & Drop or <b>Click to Upload</b>
                  </Typography>
                </label>
              ) : (
                <>
                  <img src={preview} alt="preview" style={{ maxHeight: 150 }} />

                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Button component="label">
                      Replace
                      <input
                        hidden
                        type="file"
                        onChange={(e) => handleFileChange(e.target.files[0])}
                      />
                    </Button>

                    <Button
                      color="error"
                      onClick={() => {
                        setImageFile(null);
                        setPreview('');
                      }}
                    >
                      Remove
                    </Button>
                  </Stack>
                </>
              )}
            </Box>
          </Stack>

          {/* RIGHT */}
          <Stack spacing={2} sx={{ flex: 1 }}>
            <TextField
              label="Description"
              name="description"
              value={form.description}
              onChange={handleInputChange}
              multiline
              rows={6}
              required
              fullWidth
            />

            <Stack direction="row" spacing={1}>
              <TextField
                label="Add Key Feature"
                name="keyFeatureInput"
                value={form.keyFeatureInput}
                onChange={handleInputChange}
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addKeyFeature();
                  }
                }}
              />
              <IconButton onClick={addKeyFeature}>
                <AddIcon />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              {form.keyFeatures.map((feature, idx) => (
                <Chip
                  key={idx}
                  label={feature}
                  onDelete={() => removeKeyFeature(idx)}
                  deleteIcon={<DeleteIcon />}
                />
              ))}
            </Stack>
          </Stack>
        </Stack>

        <Box mt={4} textAlign="right">
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default AddProductForm;
